/**
 * Mentor Manager
 * Tracks player activity and applies a bonus to buddy performance
 * when the player is actively fishing (active > passive incentive).
 */

import { eventBus } from '@/services/EventBus';
import { GameEvent } from '@/types';

export class MentorManager {
  private lastPlayerInteraction: number = 0;
  private isMentorActive: boolean = false;
  private readonly ACTIVITY_TIMEOUT_MS = 60_000; // 60s of inactivity = mentor off
  private readonly CHECK_INTERVAL_MS = 5_000; // Check every 5 seconds
  private checkIntervalId: number | null = null;

  /**
   * Called by FishButton, sweet spot clicks, selling, etc.
   * Marks the player as actively playing.
   */
  recordPlayerActivity(): void {
    this.lastPlayerInteraction = Date.now();
    if (!this.isMentorActive) {
      this.isMentorActive = true;
      eventBus.emit(GameEvent.MENTOR_BONUS_ACTIVATED, {});
      console.log('🎓 Mentor bonus activated — buddies get +25% catch rate');
    }
  }

  /**
   * Returns the current mentor multiplier for buddy catch chance.
   * 1.0 = no bonus, 1.25 = 25% bonus while mentoring.
   */
  getMentorMultiplier(): number {
    if (!this.isMentorActive) return 1.0;
    return 1.25;
  }

  /**
   * Quality bonus for buddies while mentoring.
   * +5% quality bonus applied to buddy fish quality calculation.
   */
  getMentorQualityBonus(): number {
    if (!this.isMentorActive) return 0;
    return 0.05;
  }

  /**
   * Check if the mentor bonus is currently active.
   */
  isActive(): boolean {
    return this.isMentorActive;
  }

  /**
   * Start the periodic activity check.
   */
  start(): void {
    if (this.checkIntervalId !== null) return;

    this.checkIntervalId = window.setInterval(() => {
      this.checkMentorStatus();
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Stop the periodic activity check.
   */
  stop(): void {
    if (this.checkIntervalId !== null) {
      window.clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  /**
   * Internal: deactivate mentor if player has been idle too long.
   */
  private checkMentorStatus(): void {
    if (this.isMentorActive && Date.now() - this.lastPlayerInteraction > this.ACTIVITY_TIMEOUT_MS) {
      this.isMentorActive = false;
      eventBus.emit(GameEvent.MENTOR_BONUS_DEACTIVATED, {});
      console.log('🎓 Mentor bonus deactivated — player idle');
    }
  }
}

// Singleton instance
export const mentorManager = new MentorManager();
